import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Ticket, CalendarDays, ScanLine, Wrench, ArrowRight, Phone, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TecnicoMobilePage() {
  const { user } = useAuth();
  const [myTickets, setMyTickets] = useState([]);
  const [myAppointments, setMyAppointments] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [tRes, aRes] = await Promise.all([
          axios.get(`${API}/tickets?stato=aperto&tecnico_id=${user?.id || ''}`).catch(() => ({ data: [] })),
          axios.get(`${API}/appuntamenti?oggi=true&tecnico_id=${user?.id || ''}`).catch(() => ({ data: [] })),
        ]);
        setMyTickets((tRes.data || []).slice(0, 5));
        setMyAppointments((aRes.data || []).slice(0, 5));
      } catch (e) {
        console.error('Errore caricamento dashboard tecnico:', e);
      }
    };
    load();
  }, [user]);

  const quickTiles = [
    {
      label: 'I miei ticket',
      sub: `${myTickets.length} aperti`,
      to: '/tickets',
      icon: Ticket,
      color: 'orange',
      testid: 'tile-tickets',
    },
    {
      label: 'Oggi',
      sub: `${myAppointments.length} appuntamenti`,
      to: '/calendario',
      icon: CalendarDays,
      color: 'cyan',
      testid: 'tile-calendario',
    },
    {
      label: 'Scansiona',
      sub: 'Barcode prodotto',
      to: '/magazzino',
      icon: ScanLine,
      color: 'violet',
      testid: 'tile-scan',
    },
    {
      label: 'Nuovo intervento',
      sub: 'Registra subito',
      to: '/tickets',
      icon: Wrench,
      color: 'emerald',
      testid: 'tile-intervento',
    },
  ];

  return (
    <div className="space-y-5 pb-20" data-testid="tecnico-mobile">
      {/* Saluto compatto */}
      <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 text-white -mx-4 -mt-4 px-4 pt-6 pb-4 md:rounded-md md:mx-0 md:mt-0">
        <p className="text-xs font-mono uppercase tracking-wider opacity-80">Modalità Tecnico</p>
        <h1 className="text-2xl font-black mt-1">Ciao {user?.nome || 'Tecnico'} 👋</h1>
        <p className="text-sm opacity-90 mt-0.5">{new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
      </div>

      {/* 4 tile principali */}
      <div className="grid grid-cols-2 gap-3">
        {quickTiles.map(t => {
          const Icon = t.icon;
          return (
            <Link to={t.to} key={t.label} data-testid={t.testid}>
              <Card className={`border-l-4 border-l-${t.color}-500 hover:shadow-md active:scale-95 transition-all cursor-pointer h-full`}>
                <CardContent className="p-4">
                  <div className={`h-10 w-10 rounded-md bg-${t.color}-500/10 flex items-center justify-center mb-2`}>
                    <Icon className={`h-5 w-5 text-${t.color}-600`} />
                  </div>
                  <h3 className="font-bold text-sm tracking-tight">{t.label}</h3>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{t.sub}</p>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* I tuoi ticket aperti */}
      {myTickets.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2 px-1">
            <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">I tuoi ticket aperti</h2>
            <Link to="/tickets" className="text-xs text-cyan-500 flex items-center gap-1">
              Tutti <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="space-y-2">
            {myTickets.map(t => (
              <Link to={`/tickets/${t.id}`} key={t.id} data-testid={`mobile-ticket-${t.id}`}>
                <Card className="hover:bg-accent/50 active:scale-[0.98] transition-all cursor-pointer">
                  <CardContent className="p-3 flex items-center gap-3">
                    <div className="h-9 w-9 rounded-md bg-orange-500/10 flex items-center justify-center flex-shrink-0">
                      <Ticket className="h-4 w-4 text-orange-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono text-muted-foreground">#{t.numero || t.id?.substring(0, 6)}</span>
                        {t.priorita && (
                          <Badge variant="outline" className="text-[9px] px-1 py-0 h-4">
                            {t.priorita}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm font-medium truncate">{t.titolo || t.descrizione || 'Ticket'}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{t.cliente_nome}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Appuntamenti di oggi */}
      {myAppointments.length > 0 && (
        <div>
          <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-2 px-1">Appuntamenti oggi</h2>
          <div className="space-y-2">
            {myAppointments.map(a => (
              <Card key={a.id} data-testid={`mobile-appuntamento-${a.id}`}>
                <CardContent className="p-3 flex items-center gap-3">
                  <div className="h-9 w-9 rounded-md bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="h-4 w-4 text-cyan-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold">{a.ora_inizio || '--:--'}</p>
                    <p className="text-xs font-medium truncate">{a.titolo}</p>
                    <p className="text-[11px] text-muted-foreground truncate">{a.cliente_nome}</p>
                  </div>
                  {a.cliente_telefono && (
                    <a href={`tel:${a.cliente_telefono}`} className="h-8 w-8 rounded-md bg-emerald-500/10 flex items-center justify-center">
                      <Phone className="h-4 w-4 text-emerald-500" />
                    </a>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {myTickets.length === 0 && myAppointments.length === 0 && (
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-sm text-muted-foreground">Nessun ticket aperto o appuntamento in giornata.</p>
            <p className="text-xs text-muted-foreground mt-1">Tocca un riquadro sopra per iniziare.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
